import React, { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import CookieConsentBanner from "./components/CookieConsentBanner";

// Eagerly load only what's needed for the first paint
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";

// Lazy-load everything else — splits each route into its own chunk
const RoleSelect = lazy(() => import("./pages/RoleSelect"));
const PlanSelect = lazy(() => import("./pages/PlanSelect"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceGuide = lazy(() => import("./pages/ResourceGuide"));
const PracticeExamsGuide = lazy(() => import("./pages/PracticeExamsGuide"));
const AiStudyGuide = lazy(() => import("./pages/AiStudyGuide"));
const ScalingAtarGuide = lazy(() => import("./pages/ScalingAtarGuide"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// Student pages
const StudentOnboarding = lazy(() => import("./pages/student/Onboarding"));
const StudentBilling = lazy(() => import("./pages/student/Billing"));
const StudentPrivacy = lazy(() => import("./pages/student/Privacy"));
const StudentDashboard = lazy(() => import("./pages/student/Dashboard"));
const StudentCourses = lazy(() => import("./pages/student/Courses"));
const StudentProgress = lazy(() => import("./pages/student/Progress"));
const StudentParentLink = lazy(() => import("./pages/student/ParentLink"));
const StudentPathways = lazy(() => import("./pages/student/Pathways"));
const StudentCourseRoadmap = lazy(() => import("./pages/student/CourseRoadmap"));
const StudentTopicDetail = lazy(() => import("./pages/student/TopicDetail"));
const StudentSchedule = lazy(() => import("./pages/student/Schedule"));
const StudentQuiz = lazy(() => import("./pages/student/Quiz"));
const StudentProfileSettings = lazy(() => import("./pages/student/ProfileSettings"));
const StudentBadges = lazy(() => import("./pages/student/Badges"));
const SectionPractice = lazy(() => import("./pages/student/SectionPractice"));
const SectionAssess = lazy(() => import("./pages/student/SectionAssess"));

// Parent pages
const ParentOnboarding = lazy(() => import("./pages/parent/Onboarding"));
const ParentChildSelector = lazy(() => import("./pages/parent/ChildSelector"));
const ParentChildDashboard = lazy(() => import("./pages/parent/ChildDashboard"));
const ParentLearningModes = lazy(() => import("./pages/parent/LearningModes"));
const ParentProfileSettings = lazy(() => import("./pages/parent/ProfileSettings"));
const ParentBilling = lazy(() => import("./pages/parent/Billing"));
const ParentPrivacy = lazy(() => import("./pages/parent/Privacy"));

// Admin pages
const AdminCourseManager = lazy(() => import("./pages/admin/CourseManager"));
const AdminCourseTools = lazy(() => import("./pages/admin/CourseTools"));
const AdminExampleLibrary = lazy(() => import("./pages/admin/ExampleLibrary"));
const AdminEmailPreview = lazy(() => import("./pages/admin/EmailPreview"));
const AdminImageAudit = lazy(() => import("./pages/admin/ImageAudit"));
const AdminTutorialPreview = lazy(() => import("./pages/admin/TutorialPreview"));
const AdminCreditAudit = lazy(() => import("./pages/admin/CreditAudit"));
const AdminSocialKit = lazy(() => import("./pages/admin/SocialKit"));
const AdminRoadmap = lazy(() => import("./pages/admin/Roadmap"));
const AdminGiftMemberships = lazy(() => import("./pages/admin/GiftMemberships"));
const AdminBugReports = lazy(() => import("./pages/admin/BugReports"));
const AdminSubjectRequests = lazy(() => import("./pages/admin/SubjectRequests"));

// Billing / help pages
const Pricing = lazy(() => import("./pages/Pricing"));
const BillingRedirect = lazy(() => import("./pages/BillingRedirect"));
const CheckoutReview = lazy(() => import("./pages/CheckoutReview"));
const SubscriptionSuccess = lazy(() => import("./pages/billing/SubscriptionSuccess"));
const HelpBilling = lazy(() => import("./pages/help/Billing"));
const HelpPrivacy = lazy(() => import("./pages/help/Privacy"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Cookies = lazy(() => import("./pages/Cookies"));

// Route-level loading fallback — minimal spinner that matches the navy theme
function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 2 * 60 * 1000, // 2 min — reduces memory pressure under concurrent load
      retry: 1,
    },
  },
});

function ProtectedRoute({ children, allowedRole }: { children: React.ReactNode; allowedRole?: string }) {
  const { user, userRole, loading, profile, profileLoaded } = useAuth();
  const location = useLocation();

  // The role lookup runs after the session is restored. Until it settles,
  // userRole is null for a signed-in user — treating that as "no role"
  // bounced every hard-loaded deep link (/student/progress) through
  // role-select and landed people on the dashboard instead.
  if (loading || (user && !profileLoaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!userRole) {
    const params = new URLSearchParams(location.search);
    const bypass = params.get('bypass');
    return <Navigate to={`/role-select${bypass === 'true' ? '?bypass=true' : ''}`} replace />;
  }

  if (allowedRole && userRole !== allowedRole) {
    if (userRole === 'admin') {
      return <Navigate to="/admin/courses" replace />;
    }
    return <Navigate to={userRole === 'student' ? '/student' : '/parent'} replace />;
  }

  if (!profile?.onboarding_completed) {
    if (userRole === 'student' && !location.pathname.includes('/student/onboarding')) {
      return <Navigate to="/student/onboarding" replace />;
    }
    if (userRole === 'parent' && !location.pathname.includes('/parent/onboarding')) {
      return <Navigate to="/parent/onboarding" replace />;
    }
  }

  return <>{children}</>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/role-select" element={<RoleSelect />} />
        <Route path="/plan-select" element={<PlanSelect />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/billing" element={<BillingRedirect />} />
        <Route path="/billing/success" element={<SubscriptionSuccess />} />
        <Route path="/checkout/review" element={<CheckoutReview />} />
        <Route path="/help/billing" element={<HelpBilling />} />
        <Route path="/help/privacy" element={<HelpPrivacy />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/tasc-practice-exams-guide" element={<PracticeExamsGuide />} />
        <Route path="/resources/tasc-ai-study-guide" element={<AiStudyGuide />} />
        <Route path="/resources/tasc-scaling-atar-guide" element={<ScalingAtarGuide />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
        <Route path="/resources/:slug" element={<ResourceGuide />} />

        {/* Student routes */}
        <Route path="/student/onboarding" element={
          <ProtectedRoute allowedRole="student"><StudentOnboarding /></ProtectedRoute>
        } />
        <Route path="/student" element={
          <ProtectedRoute allowedRole="student"><StudentDashboard /></ProtectedRoute>
        } />
        <Route path="/student/courses" element={
          <ProtectedRoute allowedRole="student"><StudentCourses /></ProtectedRoute>
        } />
        <Route path="/student/progress" element={
          <ProtectedRoute allowedRole="student"><StudentProgress /></ProtectedRoute>
        } />
        <Route path="/student/parent-link" element={
          <ProtectedRoute allowedRole="student"><StudentParentLink /></ProtectedRoute>
        } />
        <Route path="/student/pathways" element={
          <ProtectedRoute allowedRole="student"><StudentPathways /></ProtectedRoute>
        } />
        {/* Legacy practice route — redirect to roadmap */}
        <Route path="/student/practice/:courseId" element={
          <Navigate to="/student/courses" replace />
        } />
        <Route path="/student/roadmap/:courseId" element={
          <ProtectedRoute allowedRole="student"><StudentCourseRoadmap /></ProtectedRoute>
        } />
        <Route path="/student/topic/:courseId/:topicId" element={
          <ProtectedRoute allowedRole="student"><StudentTopicDetail /></ProtectedRoute>
        } />
        {/* StudyPlan consolidated into Schedule */}
        <Route path="/student/plan/:courseId" element={
          <Navigate to="/student/schedule" replace />
        } />
        <Route path="/student/schedule" element={
          <ProtectedRoute allowedRole="student"><StudentSchedule /></ProtectedRoute>
        } />
        <Route path="/student/quiz/:courseId/:topicId" element={
          <ProtectedRoute allowedRole="student"><StudentQuiz /></ProtectedRoute>
        } />
        <Route path="/student/section/:sectionId/practice" element={
          <ProtectedRoute allowedRole="student"><SectionPractice /></ProtectedRoute>
        } />
        <Route path="/student/section/:sectionId/assess" element={
          <ProtectedRoute allowedRole="student"><SectionAssess /></ProtectedRoute>
        } />
        <Route path="/student/profile" element={
          <ProtectedRoute allowedRole="student"><StudentProfileSettings /></ProtectedRoute>
        } />
        <Route path="/student/billing" element={
          <ProtectedRoute allowedRole="student"><StudentBilling /></ProtectedRoute>
        } />
        <Route path="/student/privacy" element={
          <ProtectedRoute allowedRole="student"><StudentPrivacy /></ProtectedRoute>
        } />
        <Route path="/student/badges" element={
          <ProtectedRoute allowedRole="student"><StudentBadges /></ProtectedRoute>
        } />

        {/* Parent routes */}
        <Route path="/parent/onboarding" element={
          <ProtectedRoute allowedRole="parent"><ParentOnboarding /></ProtectedRoute>
        } />
        <Route path="/parent" element={
          <ProtectedRoute allowedRole="parent"><ParentChildSelector /></ProtectedRoute>
        } />
        <Route path="/parent/child/:studentId/dashboard" element={
          <ProtectedRoute allowedRole="parent"><ParentChildDashboard /></ProtectedRoute>
        } />
        <Route path="/parent/child/:studentId/learning-modes" element={
          <ProtectedRoute allowedRole="parent"><ParentLearningModes /></ProtectedRoute>
        } />
        <Route path="/parent/child/:studentId/reports" element={
          <ProtectedRoute allowedRole="parent"><ParentChildDashboard /></ProtectedRoute>
        } />
        <Route path="/parent/profile" element={
          <ProtectedRoute allowedRole="parent"><ParentProfileSettings /></ProtectedRoute>
        } />
        <Route path="/parent/learning-modes" element={
          <ProtectedRoute allowedRole="parent"><ParentLearningModes /></ProtectedRoute>
        } />
        <Route path="/parent/billing" element={
          <ProtectedRoute allowedRole="parent"><ParentBilling /></ProtectedRoute>
        } />
        <Route path="/parent/privacy" element={
          <ProtectedRoute allowedRole="parent"><ParentPrivacy /></ProtectedRoute>
        } />

        {/* Admin routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRole="admin"><Navigate to="/admin/courses" replace /></ProtectedRoute>
        } />
        <Route path="/admin/courses" element={
          <ProtectedRoute allowedRole="admin"><AdminCourseManager /></ProtectedRoute>
        } />
        <Route path="/admin/course-tools" element={
          <ProtectedRoute allowedRole="admin"><AdminCourseTools /></ProtectedRoute>
        } />
        <Route path="/admin/example-library" element={
          <ProtectedRoute allowedRole="admin"><AdminExampleLibrary /></ProtectedRoute>
        } />
        <Route path="/admin/email-preview" element={
          <ProtectedRoute allowedRole="admin"><AdminEmailPreview /></ProtectedRoute>
        } />
        <Route path="/admin/image-audit" element={
          <ProtectedRoute allowedRole="admin"><AdminImageAudit /></ProtectedRoute>
        } />
        <Route path="/admin/tutorial-preview" element={
          <ProtectedRoute allowedRole="admin"><AdminTutorialPreview /></ProtectedRoute>
        } />
        <Route path="/admin/credit-audit" element={
          <ProtectedRoute allowedRole="admin"><AdminCreditAudit /></ProtectedRoute>
        } />
        <Route path="/admin/social-kit" element={
          <ProtectedRoute allowedRole="admin"><AdminSocialKit /></ProtectedRoute>
        } />
        <Route path="/admin/roadmap" element={
          <ProtectedRoute allowedRole="admin"><AdminRoadmap /></ProtectedRoute>
        } />
        <Route path="/admin/gift-memberships" element={
          <ProtectedRoute allowedRole="admin"><AdminGiftMemberships /></ProtectedRoute>
        } />
        <Route path="/admin/bug-reports" element={
          <ProtectedRoute allowedRole="admin"><AdminBugReports /></ProtectedRoute>
        } />
        <Route path="/admin/subject-requests" element={
          <ProtectedRoute allowedRole="admin"><AdminSubjectRequests /></ProtectedRoute>
        } />

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
            <CookieConsentBanner />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
